import { ArrowLeft, Bell, Briefcase, CheckCheck, Globe, History, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { timeAgo } from "../lib/format";
import { goBack } from "../lib/navigation";

const TYPE_LABEL = {
    new_candidate: "Nouveau candidat",
    pepite_detected: "Pépite détectée",
    urgent_call: "Appel urgent",
    immersion_scheduled: "Immersion programmée",
    immersion_confirmed: "Présence confirmée",
    immersion_cancelled: "Immersion annulée",
    validation_pending: "Validation en attente",
    validation_done: "Validation effectuée",
    // Pipeline embauche (iter 28)
    onboarding_sent: "Dossier envoyé",
    onboarding_opened: "Dossier ouvert",
    onboarding_submitted: "Dossier reçu",
    onboarding_approved: "Dossier validé",
    onboarding_needs_fix: "Correction demandée",
    onboarding_reminder: "Rappel dossier envoyé",
    contract_ready: "Contrat prêt",
    contract_sent: "Contrat envoyé",
    contract_opened: "Contrat ouvert",
    contract_signed: "Contrat signé",
    contract_voided: "Contrat annulé",
    contract_reminder: "Rappel signature envoyé",
    candidate_inactive: "Candidat inactif",
    relance_needed: "Relance nécessaire",
    relance_urgent: "Relance urgente",
    reminder_day_before: "Rappel J-1",
    new_offer_assigned: "Nouvelle offre",
};

const STATUS_LABEL = {
    new: "Nouveau",
    prerec_assigned: "À appeler",
    prerec_done: "Pré-rec faite",
    immersion_scheduled: "Immersion prévue",
    immersion_done: "Immersion faite",
    validated: "Validé",
    onboarding: "Onboarding",
    hired: "Recruté",
    rejected: "Refusé",
};

export default function Notifications() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [backfilling, setBackfilling] = useState(false);
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";

    const load = useCallback(async () => {
        const { data } = await api.get("/notifications");
        setList(data);
        setLoading(false);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const markAll = async () => {
        await api.post("/notifications/read-all");
        load();
    };

    async function handleBackfill() {
        if (backfilling) return;
        setBackfilling(true);
        try {
            const { data } = await api.post("/notifications/backfill-admin?days=90");
            if (data.created === 0) {
                toast.info("Aucune nouvelle notification à rattraper.");
            } else {
                toast.success(`${data.created} notification${data.created > 1 ? "s" : ""} rattrapée${data.created > 1 ? "s" : ""} ✅`);
            }
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors du rattrapage");
        } finally {
            setBackfilling(false);
        }
    }

    /**
     * Clicking anywhere on a notification row navigates to the candidate's
     * profile AND marks the notif as read in the background. Saves the
     * recruiter a click and gives a more obvious "this is interactive" UX.
     */
    async function openNotification(n) {
        const link = n.meta?.link;
        if (!n.candidate_id && !link) return;
        if (!n.read) {
            // Fire-and-forget — we don't want to block navigation on the PATCH
            api.post(`/notifications/${n.id}/read`).catch(() => { /* silent */ });
        }
        navigate(n.candidate_id ? `/candidats/${n.candidate_id}` : link);
    }

    return (
        <div className="space-y-4 md:space-y-6 pb-24 md:pb-8" data-testid="notifications-page">
            <button
                onClick={() => goBack(navigate, "/dashboard")}
                data-testid="notifications-back"
                title="Retour à la page précédente"
                className="inline-flex items-center gap-1.5 bg-white text-slate-600 border border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange rounded-full font-extrabold px-3 md:px-4 py-2 text-xs md:text-sm transition"
            >
                <ArrowLeft className="h-4 w-4" />
                Retour
            </button>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <h1 className="text-3xl md:text-4xl font-black tracking-tight text-shariti-black">Notifications</h1>
                    <p className="mt-1 text-sm font-medium text-slate-500">
                        {list.filter((n) => !n.read && !n.is_obsolete).length} non lue{list.filter((n) => !n.read && !n.is_obsolete).length > 1 ? "s" : ""}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {isAdmin && (
                        <button
                            onClick={handleBackfill}
                            disabled={backfilling}
                            data-testid="backfill-notifications"
                            title="Recrée les notifications des 90 derniers jours pour votre compte admin"
                            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-shariti-orange-soft text-shariti-orange border border-shariti-orange/30 hover:bg-shariti-orange hover:text-white transition font-extrabold text-sm disabled:opacity-50 disabled:cursor-wait"
                        >
                            {backfilling ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <History className="h-4 w-4" />
                            )}
                            Rattraper l&apos;historique
                        </button>
                    )}
                    <button onClick={markAll} className="btn-secondary" data-testid="mark-all-read">
                        <CheckCheck className="h-4 w-4" /> Tout marquer comme lu
                    </button>
                </div>
            </div>

            <div className="card-soft p-3 md:p-4 divide-y divide-shariti-gray">
                {loading ? (
                    <div className="p-10 flex justify-center">
                        <Loader2 className="h-5 w-5 animate-spin text-shariti-orange" />
                    </div>
                ) : list.length === 0 ? (
                    <div className="p-8 md:p-10 text-center">
                        <Bell className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                        <div className="text-sm font-semibold text-slate-400 mb-1">
                            Aucune notification pour le moment.
                        </div>
                        {isAdmin && (
                            <div className="mt-3 text-xs font-medium text-slate-500 max-w-md mx-auto">
                                Votre compte admin n&apos;a pas encore reçu de notifications.
                                Cliquez sur <strong>« Rattraper l&apos;historique »</strong> ci-dessus
                                pour générer une notification pour chaque pépite (score IA ≥ 82)
                                des 90 derniers jours.
                            </div>
                        )}
                    </div>
                ) : (
                    list.map((n) => {
                        const isInteractive = !!n.candidate_id || !!n.meta?.link;
                        const isSiteShariti = (n.candidate_offer_label || "").includes("Site Shariti");
                        return (
                            <div
                                key={n.id}
                                data-testid={`notification-${n.id}`}
                                onClick={() => openNotification(n)}
                                onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && isInteractive) { e.preventDefault(); openNotification(n); } }}
                                role={isInteractive ? "button" : undefined}
                                tabIndex={isInteractive ? 0 : undefined}
                                className={`p-4 rounded-2xl flex items-start gap-3 transition ${n.read ? "opacity-70" : ""} ${n.is_obsolete ? "bg-slate-50" : ""} ${isInteractive ? "cursor-pointer hover:bg-shariti-cream/60" : ""}`}
                            >
                                <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${n.is_obsolete ? "bg-slate-100 text-slate-400" : "bg-shariti-orange-soft text-shariti-orange"}`}>
                                    <Bell className="h-4 w-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`text-xs font-bold uppercase tracking-widest ${n.is_obsolete ? "text-slate-400 line-through" : "text-shariti-orange"}`}>
                                            {TYPE_LABEL[n.type] || n.type}
                                        </span>
                                        <span className="text-[11px] font-bold text-slate-400">· {timeAgo(n.created_at)}</span>
                                        {!n.read && !n.is_obsolete && <span className="h-1.5 w-1.5 rounded-full bg-shariti-red" />}
                                        {n.is_obsolete && (
                                            <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-200 text-slate-600" data-testid={`notif-obsolete-${n.id}`}>
                                                Obsolète
                                            </span>
                                        )}
                                    </div>
                                    <div className={`mt-1 text-sm font-extrabold ${n.is_obsolete ? "text-slate-400 line-through" : "text-shariti-black"}`}>
                                        {n.title}
                                    </div>
                                    <div className={`mt-0.5 text-sm font-medium ${n.is_obsolete ? "text-slate-400 line-through" : "text-slate-500"}`}>
                                        {n.message}
                                    </div>
                                    {/* Job offer / source line — always shown when known so the
                                        recruiter immediately knows WHICH mission the candidate
                                        is on (e.g. "Mission Itinérance" vs "Site Shariti") */}
                                    {n.candidate_offer_label && (
                                        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] font-bold">
                                            {isSiteShariti
                                                ? <Globe className="h-3 w-3 text-orange-600" />
                                                : <Briefcase className="h-3 w-3 text-blue-600" />}
                                            <span className={`px-2 py-0.5 rounded-full border ${isSiteShariti ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}
                                                data-testid={`notif-offer-${n.id}`}>
                                                {n.candidate_offer_label}
                                            </span>
                                        </div>
                                    )}
                                    {n.candidate_current_status && (
                                        <div className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                            Statut actuel :
                                            <span className="px-2 py-0.5 rounded-full bg-shariti-cream border border-shariti-gray text-shariti-black">
                                                {STATUS_LABEL[n.candidate_current_status] || n.candidate_current_status}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                {isInteractive && (
                                    <span className="self-center text-[11px] font-extrabold uppercase tracking-widest text-shariti-orange shrink-0">
                                        Ouvrir →
                                    </span>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
